import React from 'react';
import { Form, Input, InputNumber, Select, Switch, Alert } from 'antd';
import { LocalizedContentTabs, type LocalizedFieldConfig } from '../../components/LocalizedContentTabs';

/** `name` is localised; everything else on a skin is language-neutral. */
export const PET_SKIN_LANG_FIELDS: LocalizedFieldConfig[] = [
  { key: 'name', label: 'Name', required: true },
];

/**
 * Artwork paths are relative to the media root — committed files live in
 * `packages/api/prisma/pet-skin-images/` and are served from `pet-skins/`.
 * An absolute http(s) URL is passed through untouched.
 */
const PATH_HINT = 'e.g. pet-skins/egg_waffle_card.png, or a full https:// URL';

export const PetSkinFormFields: React.FC<{ slugEditable: boolean }> = ({ slugEditable }) => (
  <>
    <LocalizedContentTabs fields={PET_SKIN_LANG_FIELDS} />

    <Form.Item
      label="Slug"
      name="slug"
      rules={
        slugEditable
          ? [
              { required: true },
              {
                pattern: /^[a-z0-9]+(-[a-z0-9]+)*$/,
                message: 'Lowercase kebab-case only, e.g. egg-waffle',
              },
            ]
          : []
      }
      extra={
        slugEditable
          ? 'The public skin id the app buys and equips. It cannot be changed later.'
          : 'Fixed — players already store this id as an owned skin.'
      }
    >
      <Input disabled={!slugEditable} placeholder="egg-waffle" />
    </Form.Item>

    <Form.Item
      label="Store tile artwork"
      name="imageUrl"
      rules={[{ required: true }]}
      extra={PATH_HINT}
    >
      <Input placeholder="pet-skins/egg_waffle_card.png" />
    </Form.Item>

    <Form.Item
      label="Detail hero artwork"
      name="heroImageUrl"
      extra="The pet wearing the skin. Leave empty to reuse the tile artwork."
    >
      <Input placeholder="pet-skins/egg_waffle_hero.png" />
    </Form.Item>

    <Form.Item
      label="Pose artwork"
      name="poseImageUrls"
      extra="One path per entry — these fill the detail screen's Poses grid."
    >
      <Select mode="tags" tokenSeparators={[',']} placeholder="pet-skins/egg_waffle_pose_1.png" />
    </Form.Item>

    <Form.Item label="Price (coins)" name="priceCoins" rules={[{ required: true }]}>
      <InputNumber style={{ width: '100%' }} min={0} precision={0} />
    </Form.Item>

    <Form.Item
      label="Unlock level"
      name="unlockLevel"
      extra="Leave empty for a coins-only skin."
    >
      <InputNumber style={{ width: '100%' }} min={1} precision={0} />
    </Form.Item>

    <Form.Item label="Sort order" name="sortOrder">
      <InputNumber style={{ width: '100%' }} min={0} precision={0} />
    </Form.Item>

    <Form.Item label="Active" name="isActive" valuePropName="checked">
      <Switch />
    </Form.Item>

    <Alert
      type="info"
      showIcon
      message="Deactivating a skin only removes it from the store — players who bought it keep wearing it."
    />
  </>
);
